import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, BookOpen, Camera, Leaf, SealCheck, ShieldWarning,
} from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import SeoMeta, { breadcrumbJsonLd } from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import EntityShareCard from "@/components/EntityShareCard";
import GuideListenButton, { guideToSections } from "@/components/GuideListenButton";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

/** Canonical SpeciesPulse species profile (spec §12) — one accepted species,
 * one master record, sourced from the shared NaturePulse catalogue. */
export default function SpeciesProfilePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    setData(null); setMissing(false); setProfile(null);
    http.get(`/speciespulse/species/${slug}`).then(({ data }) => setData(data)).catch(() => setMissing(true));
    // On-demand AI field profile — generated once, cached for everyone after.
    http.get(`/speciespulse/species/${slug}/profile`).then(({ data }) => setProfile(data.profile)).catch(() => {});
  }, [slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const e = data?.species;
  const group = data?.group;

  return (
    <main className="min-h-screen bg-white text-[#14532D] pt-32 md:pt-36 pb-24" data-testid="speciespulse-species">
      {e && (
        <SeoMeta
          title={`${e.title}${e.scientific_name ? ` (${e.scientific_name})` : ""} — SpeciesPulse`}
          description={(e.summary || "").slice(0, 155)}
          canonical={`${origin}/speciespulse/species/${e.slug}`}
          ogType="article"
          jsonLd={[
            {
              "@context": "https://schema.org", "@type": "Article",
              headline: e.title, description: e.summary,
              url: `${origin}/speciespulse/species/${e.slug}`,
              mainEntity: { "@type": "Thing", name: e.title, ...(e.scientific_name ? { alternateName: e.scientific_name } : {}) },
              author: { "@type": "Organization", name: "NaturePulse" },
              isBasedOn: (e.sources || []).map((s) => s.url).filter(Boolean),
            },
            breadcrumbJsonLd([
              { name: "Home", url: origin },
              { name: "SpeciesPulse", url: `${origin}/speciespulse` },
              ...(group ? [{ name: group.label, url: `${origin}/speciespulse/group/${group.slug}` }] : []),
              { name: e.title, url: `${origin}/speciespulse/species/${e.slug}` },
            ]),
          ]}
        />
      )}
      <div className="max-w-[900px] mx-auto px-5 md:px-8">
        {missing ? (
          <div className="mt-14 text-center" data-testid="speciespulse-species-missing">
            <Leaf size={40} weight="fill" className="mx-auto text-[#166534]/40" />
            <h1 className="mt-3 font-serif-display text-3xl text-[#14532D]">Not yet documented</h1>
            <p className="mt-2 max-w-md mx-auto text-sm text-[#4B5563]">
              This species doesn't have a curated SpeciesPulse profile yet. Photograph it and run an
              identification — your observation can help start the record.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/identify" data-testid="speciespulse-missing-identify"
                className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white">
                <Camera size={13} weight="bold" /> Identify a species
              </Link>
              <Link to="/speciespulse" data-testid="speciespulse-missing-home"
                className="inline-flex items-center gap-2 rounded-full border border-[#166534]/30 px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#166534]">
                SpeciesPulse home
              </Link>
            </div>
          </div>
        ) : !e ? (
          <div className="mt-16 text-center text-sm text-[#6B7280]">Loading…</div>
        ) : (
          <>
            <Breadcrumbs items={[
              { label: "Home", to: "/" },
              { label: "SpeciesPulse", to: "/speciespulse" },
              ...(group ? [{ label: group.label, to: `/speciespulse/group/${group.slug}` }] : []),
              { label: e.title },
            ]} />

            {/* Header */}
            <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
              <div>
                {group && (
                  <Link to={`/speciespulse/group/${group.slug}`} data-testid="speciespulse-species-group"
                    className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3F6212] hover:underline">
                    {group.label}
                  </Link>
                )}
                <h1 className="mt-1 font-serif-display text-4xl md:text-5xl text-[#166534]" data-testid="speciespulse-species-title">{e.title}</h1>
                {e.scientific_name && (
                  <div className="mt-1 font-serif-display italic text-xl text-[#4B5563]" data-testid="speciespulse-species-sci">{e.scientific_name}</div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <EntityShareCard hub={e.hub} slug={e.slug} testid="speciespulse-share-card" />
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F0FDF4] border border-[#166534]/15 px-3 py-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase text-[#166534]">
                  <SealCheck size={11} weight="fill" /> Catalogue record
                </span>
              </div>
            </div>

            <ListenTranslateBar text={`${e.title}. ${e.summary || ""}${profile ? " " + [profile.intro, profile.habitat, profile.behaviour, profile.identification, ...(profile.did_you_know || []), profile.conservation].filter(Boolean).join(" ") : ""}`} testidPrefix="speciespulse-species-tools" />

            {/* Safety note for sensitive groups (fungi, reptiles) */}
            {data.safety_note && (
              <div className="mt-5 rounded-2xl border border-[#B45309]/30 bg-[#FFFBEB] p-4 flex gap-3" data-testid="speciespulse-species-safety">
                <ShieldWarning size={20} weight="fill" className="shrink-0 mt-0.5 text-[#B45309]" />
                <p className="text-sm text-[#78350F] leading-relaxed">{data.safety_note}</p>
              </div>
            )}

            {/* Summary + card fact */}
            <p className="mt-5 text-base md:text-lg text-[#1F2937] leading-relaxed" data-testid="speciespulse-species-summary">{e.summary}</p>

            {/* AI field profile (generated on first view, cached) */}
            {profile && (
              <section className="mt-6 rounded-3xl border border-black/5 bg-[#F0FDF4] p-6 md:p-8" data-testid="speciespulse-species-profile">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3F6212]">Field profile</div>
                  <GuideListenButton sections={guideToSections(profile)} testid="speciespulse-profile-listen" />
                </div>
                <p className="mt-3 text-base text-[#374151] leading-relaxed" data-testid="profile-intro">{profile.intro}</p>
                <div className="mt-5 grid gap-4 md:grid-cols-3">
                  {[["Habitat", profile.habitat], ["Behaviour", profile.behaviour], ["Identification", profile.identification]].map(([t, txt], i) => (
                    txt ? (
                      <div key={t} className="rounded-2xl bg-white/70 border border-black/5 p-4" data-testid={`profile-block-${i}`}>
                        <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3F6212]">{t}</div>
                        <p className="mt-1.5 text-sm text-[#374151] leading-relaxed">{txt}</p>
                      </div>
                    ) : null
                  ))}
                </div>
                {profile.did_you_know?.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {profile.did_you_know.map((f, i) => (
                      <li key={i} className="text-sm text-[#374151]" data-testid={`profile-fact-${i}`}>• {f}</li>
                    ))}
                  </ul>
                )}
                {profile.conservation && (
                  <p className="mt-4 text-sm text-[#4B5563] italic leading-relaxed" data-testid="profile-conservation">{profile.conservation}</p>
                )}
              </section>
            )}
{e.card_fact && (
              <div className="mt-4 rounded-2xl bg-[#F0FDF4] border border-[#166534]/10 p-4 text-sm text-[#14532D]" data-testid="speciespulse-species-fact">
                <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3F6212] block mb-1">Did you know</span>
                {e.card_fact}
              </div>
            )}

            {/* Key facts */}
            {e.key_facts?.length > 0 && (
              <div className="mt-6 grid gap-2 sm:grid-cols-2" data-testid="speciespulse-species-facts">
                {e.key_facts.map((f) => (
                  <div key={f.label} className="rounded-xl border border-black/5 bg-white p-3.5 shadow-sm">
                    <div className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#6B7280]">{f.label}</div>
                    <div className="mt-0.5 text-sm text-[#1F2937]">{f.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Sections */}
            {e.sections?.map((s) => (
              <section key={s.heading} className="mt-8" data-testid={`speciespulse-section-${s.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h2 className="font-serif-display text-2xl text-[#14532D]">{s.heading.replace(/&amp;/g, "&")}</h2>
                <div className="mt-2 text-[15px] text-[#374151] leading-relaxed" dangerouslySetInnerHTML={{ __html: s.html }} />
              </section>
            ))}

            {/* Observations */}
            <section className="mt-10" data-testid="speciespulse-species-observations">
              <div className="flex items-center justify-between">
                <h2 className="font-serif-display text-2xl text-[#14532D]">Community observations</h2>
                <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#6B7280]" data-testid="speciespulse-obs-count">
                  {data.observations_count} recorded
                </span>
              </div>
              {data.recent_observations?.length > 0 ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {data.recent_observations.map((o) => (
                    <Link key={o.id} to={`/observations/${o.id}`} data-testid={`speciespulse-species-obs-${o.id}`}
                      className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-sm hover:border-[#166534]/40 transition-colors">
                      {o.media?.[0]?.url && <img src={abs(o.media[0].url)} alt={o.title} className="h-28 w-full object-cover" loading="lazy" />}
                      <div className="p-3">
                        <div className="font-medium text-sm text-[#14532D] leading-tight">{o.title}</div>
                        <div className="mt-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase text-[#6B7280]">{o.date}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-2xl border border-dashed border-[#166534]/25 bg-[#F0FDF4]/50 p-5 text-sm text-[#4B5563]">
                  No community observations yet. Spot this species? Your photo could be the first record.
                </p>
              )}
              <Link to="/identify" data-testid="speciespulse-species-identify"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
                <Camera size={13} weight="bold" /> Report a sighting
              </Link>
            </section>

            {/* Seasonality — when this species is seen (accumulated observation dates) */}
            {data.seasonality && (
              <section className="mt-10 rounded-2xl border border-black/5 bg-white p-5 shadow-sm" data-testid="speciespulse-seasonality">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-serif-display text-2xl text-[#14532D]">When it's seen</h2>
                  {data.seasonality.this_month > 0 ? (
                    <span className="rounded-full bg-[#F0FDF4] border border-[#166534]/20 px-3 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#166534]" data-testid="speciespulse-seen-this-month">
                      Seen {data.seasonality.this_month}× this {data.seasonality.month_name}
                    </span>
                  ) : (
                    <span className="rounded-full border border-black/10 px-3 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#6B7280]" data-testid="speciespulse-seen-this-month">
                      No {data.seasonality.month_name} sightings yet
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-end gap-1.5 h-24" data-testid="speciespulse-seasonality-bars">
                  {data.seasonality.monthly.map((n, i) => {
                    const max = Math.max(...data.seasonality.monthly, 1);
                    const isNow = i === new Date().getMonth();
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${"JFMAMJJASOND"[i]} — ${n} observations`}>
                        <div
                          className={`w-full rounded-t ${isNow ? "bg-[#65A30D]" : n > 0 ? "bg-[#166534]/70" : "bg-black/8"}`}
                          style={{ height: `${Math.max((n / max) * 100, n > 0 ? 8 : 3)}%` }}
                        />
                        <span className={`font-mono-tech text-[8px] ${isNow ? "text-[#65A30D] font-bold" : "text-[#9CA3AF]"}`}>
                          {"JFMAMJJASOND"[i]}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-[#6B7280]">{data.seasonality.note}</p>
              </section>
            )}

            {/* Similar species */}
            {data.similar_species?.length > 0 && (
              <section className="mt-10" data-testid="speciespulse-species-similar">
                <h2 className="font-serif-display text-2xl text-[#14532D]">Related in {group?.label || "this group"}</h2>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {data.similar_species.map((s) => (
                    <Link key={s.slug} to={`/speciespulse/species/${s.slug}`} data-testid={`speciespulse-similar-${s.slug}`}
                      className="group rounded-xl border border-black/5 bg-white p-4 shadow-sm hover:border-[#166534]/40 transition-colors flex items-center justify-between gap-2">
                      <span>
                        <span className="block font-medium text-sm text-[#14532D] group-hover:text-[#166534]">{s.title}</span>
                        {s.scientific_name && <span className="block text-xs italic text-[#6B7280]">{s.scientific_name}</span>}
                      </span>
                      <ArrowRight size={13} weight="bold" className="text-[#166534] shrink-0" />
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Sources + provenance */}
            {e.sources?.length > 0 && (
              <section className="mt-10 rounded-2xl border border-black/5 bg-white p-5 shadow-sm" data-testid="speciespulse-species-sources">
                <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">
                  <BookOpen size={13} weight="fill" /> Sources &amp; provenance
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {e.sources.map((s) => (
                    <a key={s.url || s.label} href={s.url} target="_blank" rel="noreferrer"
                      className="rounded-full border border-black/10 px-3.5 py-1.5 text-xs text-[#166534] hover:bg-[#F0FDF4]">
                      {s.label || s.url}
                    </a>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-[#6B7280]">
                  SpeciesPulse records are curated from authoritative sources. Identification confidence,
                  conservation status and safety information always show their source — AI never invents them.
                </p>
              </section>
            )}

            {/* Catalogue cross-link */}
            <div className="mt-8 text-sm text-[#4B5563]">
              Also in the Global Environmental Catalogue:{" "}
              <Link to={`/explore/${e.hub}/${e.slug}`} data-testid="speciespulse-catalogue-link" className="text-[#166534] underline underline-offset-2">
                {e.title} — {e.hub}
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
